import { ExportOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import ListTable from "common/ListTable";
import { CUTTING_LIST_ENDPOINT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { CUTTING_CREATE_PERMISSION } from "routes/permissions";
import { cglFormatDate, hasPermission } from "utils/functions";
import style from "../production.module.css"

const Cutting = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValues, setFilterValues] = useState({});

  // Others
  const navigate = useNavigate();

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllCutting = useCallback(
    async (filterValues) => {
      setLoading(true);

      const query = `${CUTTING_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...filterValues,
      };

      const response = await getData(query, false, bodyData);
      if (response && response?.data?.code === 200) {
        console.log('response', response);
        
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.data?.per_page,
          current: response?.data?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.data?.current_page,
          total: response?.data?.data?.data?.total,
          summary: response?.data?.data?.summary ? Number(response?.data?.data?.summary) : 0,
        });
        setTableData(response?.data?.data?.data?.data);
      } else {
        setLoading(false);
        message.error("Error getting cutting data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    const existingFilter =
      JSON.parse(localStorage.getItem("cutting_filter_filter_value")) || {};

    getAllCutting(existingFilter);
  }, [refresh, getAllCutting]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const gotoAddRoute = () => {
    navigate("/production/cutting/entry");
  };

  const gotoUpdateRoute = (record) => {
    const sales_contract_id = record?.sales_contract_id;
    const style_id = record?.styles?.id;
    const buying_office_id = record?.buying_office_id;
    const cutting_date = record?.cutting_date
      ? moment(record?.cutting_date).format("YYYY-MM-DD")
      : null;
    const transaction_no_per_day = record?.transaction_no_per_day;
    const factory_id = record?.sub_contract_factory_id;

    const updateLink = `/production/cutting/update`;

    // Create an object with all the potential query parameters
    const allQueryParams = {
      sales_contract_id,
      style_id,
      buying_office_id,
      cutting_date,
      transaction_no_per_day,
      factory_id,
    };

    // Filter out null or undefined values
    const queryParams = Object.fromEntries(
        Object.entries(allQueryParams).filter(
            ([_, value]) => value !== null && value !== undefined
        )
    );

    navigate({
      pathname: updateLink,
      search: new URLSearchParams(queryParams).toString(),
    });
  };

  const tableColumns = [
    {
      title: "Buyer",
      dataIndex: ["buyer_name"],
      key: "buyer_name",
    },
    {
      title: "Style",
      dataIndex: ["styles", "style_no"],
      key: "style_no",
    },
    {
      title: "Cutting Date",
      dataIndex: ["cutting_date"],
      key: "cutting_date",
      render: (value) => cglFormatDate(value)
    },
    {
      title: "Transaction Time",
      dataIndex: ["transaction_no_per_day"],
      key: "transaction_no_per_day",
    },
    {
      title: "Cutting Day",
      dataIndex: ["cutting_day"],
      key: "cutting_day",
    },
    {
      title: "Cutting Qty",
      dataIndex: ["cutting_qty"],
      key: "cutting_qty",
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Button
            size="small"
            onClick={() => gotoUpdateRoute(record)}
            icon={<ExportOutlined />}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  const tableTitleContent = (<div className={style?.titleHeader}>
    <div className={style?.totalContainer}>
      <div className={style.totalItem}><span className={style.totalLabel}>Total Cutting: </span> {metaData?.summary} </div>
    </div>
  </div>)

  const memoizedCustomFilter = useMemo(
      () => (
        <FilterComponent
          tableData={tableData}
          filterAPI={getAllCutting}
          showFilter={showFilter}
          setShowFilter={setShowFilter}
          filterName="cutting_filter"
          filterValues={filterValues}
          setFilterValues={setFilterValues}
        />
      ),
      [showFilter],
    );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={gotoAddRoute}
                disabled={!hasPermission([CUTTING_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([CUTTING_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title="Cutting"
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                dataSource={tableData}
                columns={tableColumns}
                title={()=>tableTitleContent}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
                sticky={true}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default Cutting;
